import { useState } from 'react'

import { SkeletonRows } from '@/components/ui/skeleton'
import { type ConfirmedScope, useComplianceScope, useVaultTree, VaultFolderKey } from '@/models/vault'
import { AuditPackageButton } from './AuditPackageButton'
import { FirstAsks } from './FirstAsks'
import { FleetSummary } from './FleetSummary'
import { ScopeSetup } from './ScopeSetup'
import { TimelineCard } from './TimelineCard'
import { VaultCarrierTable } from './VaultCarrierTable'
import { useVault } from './VaultContext'
import { VaultEntityTable } from './VaultEntityTable'
import { VaultWaysIn } from './VaultWaysIn'
import { VaultWelcome } from './VaultWelcome'

export function VaultFolders() {
  const { orgId, editingScope, setEditingScope } = useVault()
  const { folders, summary, intakeEmail, isLoading } = useVaultTree(orgId)
  const { scope, isLoading: isScopeLoading } = useComplianceScope(orgId)
  const [justConfirmed, setJustConfirmed] = useState<ConfirmedScope | null>(null)

  if (isLoading || !summary || isScopeLoading) return <SkeletonRows />

  const labelFor = (key: VaultFolderKey) => folders.find((f) => f.key === key)?.label ?? ''

  // Until the carrier has signed the scope off, setting it up is the only thing on the page.
  const confirmed = !!scope?.dateConfirmed
  const settingUp = !confirmed || editingScope

  return (
    <div className="flex flex-col gap-2">
      {settingUp ? (
        <ScopeSetup
          initial={scope}
          onDone={(result) => {
            setEditingScope(false)
            setJustConfirmed(result)
          }}
          onCancel={confirmed ? () => setEditingScope(false) : undefined}
        />
      ) : null}
      {confirmed && (
        <>
          {justConfirmed && (
            <FirstAsks
              asks={justConfirmed.firstAsks}
              emailed={justConfirmed.emailed}
              contacts={justConfirmed.notified}
              onDismiss={() => setJustConfirmed(null)}
            />
          )}
          <div className="grid gap-2 lg:grid-cols-3">
            <div className="h-full lg:col-span-2">
              <VaultWelcome intakeEmail={intakeEmail} />
            </div>
            <div className="flex flex-col gap-2">
              <VaultWaysIn intakeEmail={intakeEmail} />
              <TimelineCard summary={summary} />
            </div>
          </div>
          <FleetSummary summary={summary} action={<AuditPackageButton variant="default" size="default" />} />
          <div className="flex flex-col gap-8">
            <VaultCarrierTable title={labelFor(VaultFolderKey.CARRIER)} />
            <VaultEntityTable
              kind={VaultFolderKey.DRIVERS}
              title={labelFor(VaultFolderKey.DRIVERS)}
              empty="No drivers on this carrier yet."
              itemLabel="driver"
            />
            <VaultEntityTable
              kind={VaultFolderKey.EQUIPMENT}
              title={labelFor(VaultFolderKey.EQUIPMENT)}
              empty="No trucks or trailers on this carrier yet."
              itemLabel="vehicle"
            />
          </div>
        </>
      )}
    </div>
  )
}
